// ─── Store Base URLs ────────────────────────────────────────────────────────
const STORE_URLS = {
  IN: "https://shop.betheshyft.com",
  UK: "https://shyft-uk-2.myshopify.com",
  UAE: "https://shyft-uae.myshopify.com",
};

// ─── Timezone → Country Mapping ─────────────────────────────────────────────
const TIMEZONE_MAP: Record<string, keyof typeof STORE_URLS> = {
  // India
  "Asia/Kolkata":   "IN",
  "Asia/Calcutta":  "IN",
  // United Kingdom
  "Europe/London":  "UK",
  // UAE
  "Asia/Dubai":     "UAE",
  "Asia/Muscat":    "UAE",
};

// ─── Detect Country by Timezone ─────────────────────────────────────────────
export function getCountryByTimezone(): keyof typeof STORE_URLS {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TIMEZONE_MAP[timezone] ?? "IN"; // Default to India
  } catch {
    return "IN";
  }
}

// ─── Get Store Base URL ──────────────────────────────────────────────────────
export function getStoreBaseUrl(): string {
  return STORE_URLS[getCountryByTimezone()];
}

// ─── Menu Definition per Store ───────────────────────────────────────────────

// India Store Menu
const indiaMenu = [
  {
    category: "Plant Protein Isolate",
    link: `${STORE_URLS.IN}/collections/plant-protein-isolate`,
    subCategory: [
      {
        category: "Plant Protein Isolate - Natural Cocoa",
        link: `${STORE_URLS.IN}/products/plant-protein-isolate-natural-cocoa`,
      },
      {
        category: "Plant Protein Isolate - Vanilla",
        link: `${STORE_URLS.IN}/products/plant-protein-isolate-vanilla`,
      },
      {
        category: "Plant Protein Isolate - Unflavoured",
        link: `${STORE_URLS.IN}/products/plant-protein-isolate-unflavoured`,
      },
    ],
  },
  {
    category: "Electrolytes",
    link: `${STORE_URLS.IN}/collections/electrolytes`,
    subCategory: [
      {
        category: "Rapid Hydration - Watermelon (7 Sachets)",
        link: `${STORE_URLS.IN}/products/rapid-hydration-watermelon-7-sachets`,
      },
      {
        category: "Rapid Hydration - Lime (7 Sachets)",
        link: `${STORE_URLS.IN}/products/rapid-hydration-lime-7-sachets-copy`,
      },
      {
        category: "Rapid Hydration - Strawberry (7 Sachets)",
        link: `${STORE_URLS.IN}/products/rapid-hydration-strawberry-7-sachets`,
      },
      {
        category: "Rapid Hydration - Unflavoured (7 Sachets)",
        link: `${STORE_URLS.IN}/products/rapid-hydration-unflavoured-7-sachets`,
      },
      {
        category: "Rapid Hydration - Unflavoured (25 Sachets)",
        link: `${STORE_URLS.IN}/products/rapid-hydration-unflavoured-25-sachets`,
      },
      {
        category: "Rapid Hydration - Strawberry (25 Sachets)",
        link: `${STORE_URLS.IN}/products/rapid-hydration-stawberry-25-sachets`,
      },
      {
        category: "Rapid Hydration - Lime (25 Sachets)",
        link: `${STORE_URLS.IN}/products/rapid-hydration-lime`,
      },
      {
        category: "Rapid Hydration - Mixed Flavours (9 Sachets)",
        link: `${STORE_URLS.IN}/collections/electrolytes/products/rapid-hydration-mixed-flavours-9-sachets`,
      },
    ],
  },
  {
    category: "Fermented Yeast Protein",
    link: `${STORE_URLS.IN}/collections/fermented-yeast-protein`,
    subCategory: [
      {
        category: "Yeast Fermented Protein - Chocolate",
        link: `${STORE_URLS.IN}/collections/all/products/fermented-yeast-protein-chocolate`,
      },
      {
        category: "Yeast Fermented Protein - Unflavoured",
        link: `${STORE_URLS.IN}/collections/all/products/fermented-yeast-protein-unflavoured`,
      },
    ],
  },
  {
    category: "Protein Bars",
    link: `${STORE_URLS.IN}/collections/protein-bars`,
    subCategory: [
      {
        category: "Vegan Protein Bar: 3 flavours",
        link: `${STORE_URLS.IN}/products/shyft-vegan-protein-bar-3-flavours`,
      },
      {
        category: "Vegan Protein Bar: Berry Cacao",
        link: `${STORE_URLS.IN}/products/shyft-vegan-protein-bar-berry-cacao`,
      },
      {
        category: "Vegan Protein Bar: Almond Cacao",
        link: `${STORE_URLS.IN}/products/shyft-vegan-protein-bar-almond`,
      },
      {
        category: "Vegan Protein Bar: Peanut Cacao",
        link: `${STORE_URLS.IN}/products/shyft-vegan-protein-bar-peanut`,
      },
    ],
  },
  {
    category: "Accessories",
    link: `${STORE_URLS.IN}/collections/accessories`,
    subCategory: [
      {
        category: "Premium Yoga Mats",
        link: `${STORE_URLS.IN}/products/premium-yoga-mats`,
      },
      {
        category: "Shyft Stainless Steel Shaker",
        link: `${STORE_URLS.IN}/products/shyft-shaker`,
      },
    ],
  },
  {
    category: "Nutrition",
    link: "/plans/nutrition/",
    // subCategory: [
    //   {
    //     category: "Nutrition for Adults",
    //     link: "/plans/nutrition/",
    //   },
    //   {
    //     category: "Nutrition for Kids",
    //     link: "/nutrition-for-kids/",
    //   },
    // ],
  },
  {
    category: "Blog",
    link: "https://www.betheshyft.com/healthwatch/",
    id: "header-healthwatch-blog-link",
    query: {
      utm_source: "shyft_website",
      utm_medium: "shyft_website_link",
      adset_name: "website_link",
    },
    isExternal: true,
  },
];

// UK Store Menu
const ukMenu = [
  {
    category: "Plant Protein Isolate",
    link: `${STORE_URLS.UK}/collections/plant-protein-isolate`,
    subCategory: [
      {
        category: "Plant Protein Isolate - Natural Cocoa",
        link: `${STORE_URLS.UK}/products/plant-protein-isolate-natural-cocoa`,
      },
      {
        category: "Plant Protein Isolate - Vanilla",
        link: `${STORE_URLS.UK}/products/plant-protein-isolate-vanilla`,
      },
      {
        category: "Plant Protein Isolate - Unflavoured",
        link: `${STORE_URLS.UK}/products/plant-protein-isolate-unflavoured`,
      },
      {
        category: "Yeast Fermented Protein - Chocolate",
        link: `${STORE_URLS.UK}/collections/protein-powder-flavours/products/fermented-yeast-protein-chocolate`,
      },
      {
        category: "Yeast Fermented Protein - Unflavoured",
        link: `${STORE_URLS.UK}/collections/all/products/fermented-yeast-protein-unflavoured`,
      },
    ],
  },
  {
    category: "Electrolytes",
    link: `${STORE_URLS.UK}/collections/electrolytes`,
    subCategory: [
      {
        category: "Rapid Hydration - Watermelon (7 Sachets)",
        link: `${STORE_URLS.UK}/products/rapid-hydration-watermelon-7-sachets`,
      },
      {
        category: "Rapid Hydration - Lime (7 Sachets)",
        link: `${STORE_URLS.UK}/products/rapid-hydration-lime-7-sachets-copy`,
      },
      {
        category: "Rapid Hydration - Strawberry (7 Sachets)",
        link: `${STORE_URLS.UK}/products/rapid-hydration-strawberry-7-sachets`,
      },
      {
        category: "Rapid Hydration - Unflavoured (7 Sachets)",
        link: `${STORE_URLS.UK}/products/rapid-hydration-unflavoured-7-sachets`,
      },
      {
        category: "Rapid Hydration - Unflavoured (25 Sachets)",
        link: `${STORE_URLS.UK}/products/rapid-hydration-unflavoured-25-sachets`,
      },
      {
        category: "Rapid Hydration - Strawberry (25 Sachets)",
        link: `${STORE_URLS.UK}/products/rapid-hydration-stawberry-25-sachets`,
      },
      {
        category: "Rapid Hydration - Lime (25 Sachets)",
        link: `${STORE_URLS.UK}/products/rapid-hydration-lime`,
      },
      {
        category: "Rapid Hydration - Mixed Flavours (9 Sachets)",
        link: `${STORE_URLS.UK}/collections/electrolytes/products/rapid-hydration-mixed-flavours-9-sachets`,
      },
    ],
  },
  {
    category: "Fermented Yeast Protein",
    link: `${STORE_URLS.UK}/collections/fermented-yeast-protein`,
    subCategory: [
      {
        category: "Yeast Fermented Protein - Chocolate",
        link: `${STORE_URLS.UK}/collections/all/products/fermented-yeast-protein-chocolate`,
      },
      {
        category: "Yeast Fermented Protein - Unflavoured",
        link: `${STORE_URLS.UK}/collections/all/products/fermented-yeast-protein-unflavoured`,
      },
    ],
  },
  {
    category: "Protein Bars",
    link: `${STORE_URLS.UK}/collections/protein-bars`,
    subCategory: [
      {
        category: "Vegan Protein Bar: 3 flavours",
        link: `${STORE_URLS.UK}/products/shyft-vegan-protein-bar-3-flavours`,
      },
      {
        category: "Vegan Protein Bar: Berry Cacao",
        link: `${STORE_URLS.UK}/products/shyft-vegan-protein-bar-berry-cacao`,
      },
      {
        category: "Vegan Protein Bar: Almond Cacao",
        link: `${STORE_URLS.UK}/products/shyft-vegan-protein-bar-almond`,
      },
      {
        category: "Vegan Protein Bar: Peanut Cacao",
        link: `${STORE_URLS.UK}/products/shyft-vegan-protein-bar-peanut`,
      },
    ],
  },
  {
    category: "Accessories",
    link: `${STORE_URLS.UK}/collections/accessories`,
    subCategory: [
      {
        category: "Premium Yoga Mats",
        link: `${STORE_URLS.UK}/products/premium-yoga-mats`,
      },
      {
        category: "Shyft Stainless Steel Shaker",
        link: `${STORE_URLS.UK}/products/shyft-shaker`,
      },
    ],
  },
  {
    category: "Nutrition",
    subCategory: [
      {
        category: "Nutrition for Adults",
        link: "/plans/nutrition/",
      },
      {
        category: "Nutrition for Kids",
        link: "/nutrition-for-kids/",
      },
    ],
  },
  {
    category: "Blog",
    link: "https://www.betheshyft.com/healthwatch/",
    id: "header-healthwatch-blog-link",
    query: {
      utm_source: "shyft_website",
      utm_medium: "shyft_website_link",
      adset_name: "website_link",
    },
    isExternal: true,
  },
];

// UAE Store Menu
const uaeMenu = [
  {
    category: "Plant Protein Isolate",
    link: `${STORE_URLS.UAE}/collections/plant-protein-isolate`,
    subCategory: [
      {
        category: "Plant Protein Isolate - Natural Cocoa",
        link: `${STORE_URLS.UAE}/products/plant-protein-isolate-natural-cocoa`,
      },
      {
        category: "Plant Protein Isolate - Vanilla",
        link: `${STORE_URLS.UAE}/products/plant-protein-isolate-vanilla`,
      },
      {
        category: "Plant Protein Isolate - Unflavoured",
        link: `${STORE_URLS.UAE}/products/plant-protein-isolate-unflavoured`,
      },
      {
        category: "Yeast Fermented Protein - Chocolate",
        link: `${STORE_URLS.UAE}/collections/all/products/fermented-yeast-protein-chocolate`,
      },
      {
        category: "Yeast Fermented Protein - Unflavoured",
        link: `${STORE_URLS.UAE}/collections/all/products/fermented-yeast-protein-unflavoured`,
      },
    ],
  },
  {
    category: "Electrolytes",
    link: `${STORE_URLS.UAE}/collections/electrolytes`,
    subCategory: [
      {
        category: "Rapid Hydration - Watermelon (7 Sachets)",
        link: `${STORE_URLS.UAE}/products/rapid-hydration-watermelon-7-sachets`,
      },
      {
        category: "Rapid Hydration - Lime (7 Sachets)",
        link: `${STORE_URLS.UAE}/products/rapid-hydration-lime-7-sachets-copy`,
      },
      {
        category: "Rapid Hydration - Strawberry (7 Sachets)",
        link: `${STORE_URLS.UAE}/products/rapid-hydration-strawberry-7-sachets`,
      },
      {
        category: "Rapid Hydration - Unflavoured (7 Sachets)",
        link: `${STORE_URLS.UAE}/products/rapid-hydration-unflavoured-7-sachets`,
      },
      {
        category: "Rapid Hydration - Unflavoured (25 Sachets)",
        link: `${STORE_URLS.UAE}/products/rapid-hydration-unflavoured-25-sachets`,
      },
      {
        category: "Rapid Hydration - Strawberry (25 Sachets)",
        link: `${STORE_URLS.UAE}/products/rapid-hydration-stawberry-25-sachets`,
      },
      {
        category: "Rapid Hydration - Lime (25 Sachets)",
        link: `${STORE_URLS.UAE}/products/rapid-hydration-lime`,
      },
      {
        category: "Rapid Hydration - Mixed Flavours (9 Sachets)",
        link: `${STORE_URLS.UAE}/collections/electrolytes/products/rapid-hydration-mixed-flavours-9-sachets`,
      },
    ],
  },
  {
    category: "Fermented Yeast Protein",
    link: `${STORE_URLS.UAE}/collections/fermented-yeast-protein`,
    subCategory: [
      {
        category: "Yeast Fermented Protein - Chocolate",
        link: `${STORE_URLS.UAE}/collections/all/products/fermented-yeast-protein-chocolate`,
      },
      {
        category: "Yeast Fermented Protein - Unflavoured",
        link: `${STORE_URLS.UAE}/collections/all/products/fermented-yeast-protein-unflavoured`,
      },
    ],
  },
  {
    category: "Protein Bars",
    link: `${STORE_URLS.UAE}/collections/protein-bars`,
    subCategory: [
      {
        category: "Vegan Protein Bar: 3 flavours",
        link: `${STORE_URLS.UAE}/products/shyft-vegan-protein-bar-3-flavours`,
      },
      {
        category: "Vegan Protein Bar: Berry Cacao",
        link: `${STORE_URLS.UAE}/products/shyft-vegan-protein-bar-berry-cacao`,
      },
      {
        category: "Vegan Protein Bar: Almond Cacao",
        link: `${STORE_URLS.UAE}/products/shyft-vegan-protein-bar-almond`,
      },
      {
        category: "Vegan Protein Bar: Peanut Cacao",
        link: `${STORE_URLS.UAE}/products/shyft-vegan-protein-bar-peanut`,
      },
    ],
  },
  {
    category: "Accessories",
    link: `${STORE_URLS.UAE}/collections/accessories`,
    subCategory: [
      {
        category: "Premium Yoga Mats",
        link: `${STORE_URLS.UAE}/products/premium-yoga-mats`,
      },
      {
        category: "Shyft Stainless Steel Shaker",
        link: `${STORE_URLS.UAE}/products/shyft-shaker`,
      },
    ],
  },
  {
    category: "Nutrition",
    subCategory: [
      {
        category: "Nutrition for Adults",
        link: "/plans/nutrition/",
      },
      {
        category: "Nutrition for Kids",
        link: "/nutrition-for-kids/",
      },
    ],
  },
  {
    category: "Blog",
    link: "https://www.betheshyft.com/healthwatch/",
    id: "header-healthwatch-blog-link",
    query: {
      utm_source: "shyft_website",
      utm_medium: "shyft_website_link",
      adset_name: "website_link",
    },
    isExternal: true,
  },
];

// ─── Menu Map ────────────────────────────────────────────────────────────────
const MENU_MAP: Record<keyof typeof STORE_URLS, typeof indiaMenu> = {
  IN:  indiaMenu,
  UK:  ukMenu,
  UAE: uaeMenu,
};

// ─── Dynamic Menu Export ─────────────────────────────────────────────────────
export const menu = MENU_MAP[getCountryByTimezone()];

// ─── ShyftPass & Profile Menus (unchanged) ───────────────────────────────────
export const ShyftPassPageMenu = [
  {
    title: "Home",
    link: "shyft-pass-home",
    id: "1",
  },
  {
    title: "How it works",
    link: "shyft-pass-how-it-works",
    id: "2",
  },
  {
    title: "Schedule",
    link: "shyft-pass-workout-schedule",
    id: "3",
  },
  {
    title: "FAQ",
    link: "shyft-pass-faq",
    id: "4",
  },
];

export const profileMenu = [
  {
    category: "Profile",
    link: "/profile/",
    isExternal: true,
  },
  {
    category: "Contact us",
    link: "/contact-us/",
  },
  {
    category: "Logout",
    isLogoutLink: true,
  },
];

export default menu;

